import { useState } from 'react';
import { LedgerPanel } from '@/billing/LedgerPanel';
import { InvoicesPanel } from '@/billing/InvoicesPanel';
import { BillingDetailsPanel } from '@/billing/BillingDetailsPanel';

/**
 * The client's own money.
 *
 * Three tabs rather than three destinations, for the same reason Team has three:
 * they are all answers to "what is this account spending", and a rail that grows an
 * entry per settings screen becomes one nobody reads.
 */
type Tab = 'ledger' | 'invoices' | 'details';

const TABS: { key: Tab; label: string; blurb: string }[] = [
  {
    key: 'ledger',
    label: 'Balance',
    blurb: 'What is left, what has been spent, and what has been added. Every line is a message '
      + 'or a payment — nothing is rolled up, so a figure you disagree with can always be taken apart.',
  },
  {
    key: 'invoices',
    label: 'Invoices',
    blurb: 'A GST tax invoice for every top-up, with your GSTIN and ours. One per payment, not per '
      + 'message — the taxable supply is the money you add, not each thing you spend it on.',
  },
  {
    key: 'details',
    label: 'Billing details',
    blurb: 'The name, GSTIN and address that appear on your invoices. Your state decides whether '
      + 'they carry CGST and SGST or IGST, so it is the one field worth checking twice.',
  },
];

export function BillingPage() {
  const [tab, setTab] = useState<Tab>('ledger');
  const current = TABS.find((t) => t.key === tab)!;

  return (
    <div className="pane-scroll h-full overflow-y-auto bg-(--color-paper)">
      <div className="@container mx-auto flex max-w-4xl flex-col gap-5 p-3 sm:p-5">
        <header className="flex flex-col gap-3">
          <h1 className="text-title font-semibold tracking-tight">Billing</h1>

          <div role="tablist" aria-label="Billing" className="chip-scroll flex items-center gap-1">
            {TABS.map((item) => (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={tab === item.key}
                onClick={() => setTab(item.key)}
                className={[
                  'tap shrink-0 rounded-(--radius-chip) px-2.5 py-1 text-micro font-medium transition',
                  tab === item.key
                    ? 'bg-(--color-brand) text-(--color-brand-ink)'
                    : 'text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink)',
                ].join(' ')}
              >
                {item.label}
              </button>
            ))}
          </div>

          <p className="max-w-prose text-small leading-relaxed text-(--color-muted)">{current.blurb}</p>
        </header>

        {tab === 'ledger' && <LedgerPanel />}
        {tab === 'invoices' && <InvoicesPanel />}
        {tab === 'details' && <BillingDetailsPanel />}
      </div>
    </div>
  );
}
